/**
 * Dashboard (ZOY-702) — management reports. Wording follows the backend's
 * own terms: charged, collected, debt, credit. Never "revenue". Nothing here
 * reads as a rate the payload cannot support, and practitioner volume is
 * never worded as a ranking.
 */
export const reportsEn: Record<string, string> = {
  'nav.dashboard': 'Dashboard',
  'rp.title': 'Dashboard',
  'rp.sub':
    'How the clinic is doing over the selected months — activity, money, patients and time off. Totals only; no patient is named here.',
  'rp.range.label': 'Period',
  'rp.range.3m': '3 months',
  'rp.range.6m': '6 months',
  'rp.range.12m': '12 months',
  'rp.range.year': 'This year',
  'rp.range.custom': 'Custom',
  'rp.range.from': 'From month',
  'rp.range.to': 'To month',
  'rp.range.apply': 'Show',
  'rp.range.order': 'The first month must not be after the last one.',
  'rp.range.tooWide': 'A period can cover at most {max, number} months.',
  'rp.range.showing': '{from} – {to}',
  'rp.fresh': 'Figures as of {time}',
  'rp.fresh.patients': 'patient figures as of {time}',
  'rp.export.cta': 'Export for Excel',
  'rp.export.title': 'Zoyaar Clinic — Dashboard',
  'rp.export.window': 'Period',
  'rp.export.generated': 'Figures as of',
  'rp.export.figure': 'Figure',
  'rp.export.value': 'Value',
  'rp.export.patients': 'Patients',
  'rp.failedTitle': 'The dashboard could not be loaded',
  'rp.failedBody': 'Nothing is shown rather than something out of date. Try again in a moment.',
  'rp.retry': 'Try again',
  'rp.empty': 'Nothing in the selected period.',
  'rp.sortBy': 'Sort by {col}',
  'rp.sortedAsc': 'sorted ascending',
  'rp.sortedDesc': 'sorted descending',
  'rp.wholePeriod': 'Whole period',
  'rp.month': 'Month',

  'rp.now.title': 'Right now',
  'rp.now.note': 'As of now — not tied to the selected months.',
  'rp.now.debt': 'Debt',
  'rp.now.credit': 'Credit',
  'rp.now.debtPatients':
    '{n, plural, =0 {no patient owes} one {# patient owes} other {# patients owe}}',
  'rp.now.creditPatients':
    '{n, plural, =0 {no patient in credit} one {# patient in credit} other {# patients in credit}}',
  'rp.now.drafts': 'Assessment drafts in progress',
  'rp.now.balances': 'Patient balances',

  'rp.g.activity': 'Activity',
  'rp.g.money': 'Money',
  'rp.g.growth': 'Patients and assessments',
  'rp.g.capacity': 'Time off',

  'rp.svc.title': 'Delivered sessions by service',
  'rp.svc.sub': 'Sessions delivered each month; every attendee of a group session counts here.',
  'rp.svc.other': 'Other services',
  'rp.svc.filter': 'Service',
  'rp.svc.all': 'All services',
  'rp.paid.note':
    'Paid so far is what has been paid towards these sessions up to now. It can still rise as earlier sessions are paid — it is not a final figure.',

  'rp.pr.title': 'Sessions by practitioner',
  'rp.pr.sub': 'Session volume for planning. A group session counts once for its practitioner.',
  'rp.pr.note': 'A group session counts once, however many attended.',

  'rp.cx.title': 'Cancellations and no-shows',
  'rp.cx.sub':
    'Counts by the month the session was scheduled for. Cancelled and no-show are kept apart.',
  'rp.cx.note': 'Counts only, by scheduled month.',
  'rp.cx.byPractitioner': 'Cancellations and no-shows by practitioner',
  'rp.cx.pickMonth': 'Choose one month to open its bookings in the calendar.',
  'rp.cx.openCancelled': 'Open the cancelled bookings of {name} in the calendar',
  'rp.cx.openNoShow': 'Open the no-shows of {name} in the calendar',

  'rp.fin.chargedTitle': 'Charged and paid so far',
  'rp.fin.chargedSub':
    'What delivered sessions were charged, and how much of it has been paid up to now.',
  'rp.fin.collectedTitle': 'Collected by method',
  'rp.fin.collectedSub': 'Payments received each month, by the month they were received.',
  'rp.fin.reversedNote':
    'Reversed payments are stated apart and never subtracted from what was collected.',
  'rp.fin.history': 'Payments',
  'rp.fin.historyOf': 'Open the payments received in {month}',
  'rp.fin.totalCharged': 'Charged',
  'rp.fin.totalPaid': 'Paid so far',
  'rp.fin.totalCollected': 'Collected',
  'rp.fin.totalReversed': 'Reversed',

  'rp.pt.title': 'New contacts and registrations',
  'rp.pt.sub':
    'People first recorded each month, split by what they are today, beside the registrations completed that month.',
  'rp.pt.note':
    '“Still guests” is today’s status: recent months have had less time to register, so months are not comparable as a rate.',
  'rp.pt.totalRegistered': 'Registered patients (all time)',
  'rp.pt.totalGuests': 'Guests (all time)',

  'rp.as.title': 'Assessments',
  'rp.as.sub':
    'Assessments completed each month, split by whether a package was created from them, and the drafts started that month that are still drafts.',

  'rp.to.title': 'Practitioner time off',
  'rp.to.sub': 'Every time-off entry in the period, with its hours.',
  'rp.to.truncated':
    'This list is cut short: the period has more time-off entries than can be listed at once, and the newest are missing. Choose a shorter period to see them all.',
  'rp.to.filter': 'Practitioner',
  'rp.to.all': 'All practitioners',
  'rp.to.total': '{hours} hours in {n, plural, one {# entry} other {# entries}}',
  'rp.to.reasonShow': 'Show reason',
  'rp.to.reasonHide': 'Hide reason',
  'rp.to.reasonOf': 'Reason for the time off of {name} on {date}',
  'rp.to.noReason': 'No reason was recorded.',
  'rp.to.availability': 'Availability',

  'rp.col.month': 'Month',
  'rp.col.service': 'Service',
  'rp.col.sessions': 'Sessions',
  'rp.col.charged': 'Charged',
  'rp.col.paid': 'Paid so far',
  'rp.col.paidPct': 'Paid so far %',
  'rp.col.practitioner': 'Practitioner',
  'rp.col.individual': 'Individual sessions',
  'rp.col.group': 'Group sessions',
  'rp.col.total': 'Total',
  'rp.col.cancelled': 'Cancelled',
  'rp.col.noShow': 'No-show',
  'rp.col.collected': 'Collected',
  'rp.col.payments': 'Payments',
  'rp.col.reversed': 'Reversed',
  'rp.col.reversedCount': 'Reversed payments',
  'rp.col.newContacts': 'New contacts',
  'rp.col.registeredNow': 'Registered now',
  'rp.col.stillGuests': 'Still guests',
  'rp.col.registrations': 'Registrations',
  'rp.col.completed': 'Completed',
  'rp.col.linked': 'Linked to a package',
  'rp.col.notLinked': 'Not linked',
  'rp.col.drafts': 'Drafts started',
  'rp.col.date': 'Date',
  'rp.col.dateGregorian': 'Date (Gregorian)',
  'rp.col.hours': 'Hours',

  'perm.clinic.reports.read': 'View the management dashboard',
  'perm.clinic.reports.read.desc':
    'Opens the Dashboard: clinic-wide totals for sessions, money, patients, assessments, cancellations and time off. No patient is named; opening patient-level pages still needs their own permissions.',

  'rp.g.feedback': 'Family feedback',
  'rp.fb.title': 'Family feedback by therapist',
  'rp.fb.sub': 'Ratings are 1 to 5. Select a therapist to read the feedback.',
  'rp.fb.totals': '{responses} responses · overall average {avg} · response rate {rate}',
  'rp.fb.none': 'No feedback in this period yet.',
  'rp.fb.col.responses': 'Responses',
  'rp.fb.col.rate': 'Response rate',
  'rp.fb.col.avg': 'Average',
  'rp.fb.q.overall': 'Overall satisfaction',
  'rp.fb.q.manner': 'Manner and attention',
  'rp.fb.q.clarity': 'Clarity of explanation',
  'rp.fb.q.punctuality': 'Punctuality',
  'rp.fb.from': 'From {n} responses',
  'rp.fb.rateOf': '{responses} responses of {eligible} sessions',
  'rp.fb.unnamed': 'Unnamed',
  'rp.fb.listTitle': 'Feedback for {name}',
  'rp.fb.listAll': 'All feedback',
  'rp.fb.back': 'Close list',
  'rp.fb.onlyComments': 'Only with a comment',
  'rp.fb.col.date': 'Session date',
  'rp.fb.col.patient': 'Patient',
  'rp.fb.col.service': 'Service',
  'rp.fb.col.score': 'Overall',
  'rp.fb.col.comment': 'Comment',
  'rp.fb.noComment': 'No comment',
  'rp.fb.listEmpty': 'No feedback matches.',
  'rp.fb.open': 'Open feedback',
  'rp.fb.sheet.title': 'Family feedback',
  'rp.fb.sheet.close': 'Close',
  'rp.fb.sheet.by': 'Given by',
  'rp.fb.sheet.patient': 'Patient',
  'rp.fb.sheet.record': 'Record {n}',
  'rp.fb.sheet.openPatient': 'Open the patient record',
  'rp.fb.sheet.session': 'Session',
  'rp.fb.sheet.therapist': 'Therapist',
  'rp.fb.sheet.submitted': 'Submitted',
  'rp.fb.sheet.comment': 'Comment',
  'rp.fb.sheet.voided':
    'This session was voided later; its feedback is not counted in the figures.',
  'rp.fb.sheet.failed': 'This feedback could not be loaded.',
  'rp.fb.outOf': '{n} of 5',

  'perm.clinic.feedback.read': 'View family feedback',
  'perm.clinic.feedback.read.desc':
    'Shows what families said about sessions on the Dashboard: a summary per therapist and each feedback with the patient and family member named. Give it only to whoever follows these up; Dashboard access is also required.',

  'rp.g.endings': 'Dropout and discharge',
  'rp.te.title': 'Dropouts and discharges by month',
  'rp.te.sub': 'Final records only. Discharges are counted separately from dropouts.',
  'rp.te.dropouts': 'Dropouts',
  'rp.te.discharges': 'Discharges',
  'rp.te.returns': 'Returns',
  'rp.te.totals': '{dropouts} dropouts · {discharges} discharges · {returns} returns',
  'rp.te.pending': '{n} waiting for settlement — not counted yet.',
  'rp.te.none': 'No dropout or discharge became final in this period.',
  'rp.te.rate': 'Dropout rate',
  'rp.te.rateHelp': '{dropouts} dropouts of {active} patients with a session',
  'rp.te.reasonTitle': 'Reasons for dropping out',
  'rp.te.col.reason': 'Reason',
  'rp.te.col.count': 'Count',
  'rp.te.col.month': 'Month',
  'rp.te.practitionerTitle': 'By therapist',
  'rp.te.serviceTitle': 'By service',
  'rp.te.col.service': 'Service',
  'rp.te.sessionsTitle': 'Dropout after how many sessions',
  'rp.te.col.sessions': 'Sessions delivered',
  'rp.te.originTitle': 'When they dropped out',
  'rp.te.origin.mid_course': 'Mid-course',
  'rp.te.origin.not_continued': 'Did not continue after the course',
  'rp.te.unknown': 'Unknown',
  // ZOY-776: extra charges.
  'rp.xc.title': 'Extra charges',
  'rp.xc.sub':
    'Charges beside sessions by kind — count, amount, paid so far, minutes and off-default entries (a mark, no approval).',
  'rp.xc.note':
    'Off default = the amount differed from the kind’s default, or the kind had a free amount.',
  'rp.col.kind': 'Kind',
  'rp.col.count': 'Count',
  'rp.col.minutes': 'Minutes',
  'rp.col.flagged': 'Off default',
  'rp.col.extraMinutes': 'Extra minutes',
  'rp.pr.extraNote':
    '“Extra minutes” are the minutes of extra charges whose kind counts as practitioner working time.',
};
